import { request } from "@shared/utils";
import { API, METHODS } from "@shared/constants";
import {
  GetPayPerLeadShape,
  LeadPriceResponse,
  PayPerLeadSubscription,
  SubscriptionServerModel,
  GetDisputesRequest,
  UpdateDisputeRequest,
  QualifyLeadsRequest,
  CheckPurchaseResponse,
  DownloadLeadsRequestPayload,
  UpdateDisputeResponse,
  UpdateSubscriptionBillingPayload,
  DisputePayPerLeadBody,
  GetSubscriptionStatisticsRequest,
  ChargeQuote,
  SetDiscountPayload,
  StateIdType,
  ApplyNewPrices,
  BuyerRequest,
  GetPayPerLeadSubscriptionsListShape,
} from "@containers/PayPerLead/interface";
import { SubscriptionConverter } from "@containers/PayPerLead/api/converter";
import { SubscriptionsListQuery, SubscriptionsListResponse } from "@containers/PayPerLead/interface/Actions";
import { BaseFilter, PayPerLeadCountiesFilter, PayPerLeadStatisticsFilter } from "@shared/interfaces";
import { PayPerLeadBuyerProfile } from "@containers/PayPerLead/interface/PayPerLeadBuyerProfile";

interface List<T> {
  items: T[];
  total: number;
}

export default {
  getLead: (id: string) =>
    request(
      METHODS.GET,
      API.PAY_PER_LEAD.GET_LEAD_BY_ID(id),
    )({
      params: {
        show_detail: true,
        show_dispute: true,
        show_state: true,
        show_county: true,
        show_forwarding: true,
        show_account: true,
      },
    }),
  disputeLead: (body: DisputePayPerLeadBody) => request(METHODS.POST, API.PAY_PER_LEAD.DISPUTE_LEAD)(body),
  getLeads: (params: GetPayPerLeadShape) => request(METHODS.GET, API.PAY_PER_LEAD.GET_LEADS)({ params }),
  getLeadsCounties: (params: PayPerLeadCountiesFilter) =>
    request(METHODS.GET, API.PAY_PER_LEAD.GET_LEADS_COUNTIES)({ params }),
  getSubscriptionsList: (params: GetPayPerLeadSubscriptionsListShape) =>
    request(METHODS.GET, API.PAY_PER_LEAD.GET_SUBSCRIPTIONS)({ params }),
  getLeadPrice: (state?: number | StateIdType, county?: number): Promise<LeadPriceResponse> =>
    request(
      METHODS.GET,
      API.PAY_PER_LEAD.GET_LEAD_PRICE,
    )({
      params: {
        stateId: state === StateIdType.Nationwide ? undefined : state,
        countyId: county,
      },
    }),
  getSubscriptions: async (query: SubscriptionsListQuery): Promise<SubscriptionsListResponse> => {
    const response: List<SubscriptionServerModel> = await request(
      METHODS.GET,
      API.PAY_PER_LEAD.GET_SUBSCRIPTIONS_LIST,
    )({
      params: {
        is_active: query.isActive,
        show_items: query.showItems,
        order: query.order,
        direction: query.direction,
      },
    });

    return {
      total: response.total,
      items: response.items.map(SubscriptionConverter.fromServerModel),
    };
  },
  getSubscriptionDetails: async (id: number, draftItems: boolean): Promise<PayPerLeadSubscription> => {
    const response: SubscriptionServerModel = await request(
      METHODS.GET,
      API.PAY_PER_LEAD.GET_SUBSCRIPTION(id),
    )({ params: { show_items: true, draft_items: draftItems } });

    return SubscriptionConverter.fromServerModel(response);
  },
  createSubscription: async (accountId: number, subscription: PayPerLeadSubscription) => {
    const serverModel = SubscriptionConverter.toServerModel(subscription, accountId);
    const response: SubscriptionServerModel = await request(
      METHODS.POST,
      API.PAY_PER_LEAD.CREATE_SUBSCRIPTION,
    )(serverModel);

    return SubscriptionConverter.fromServerModel(response);
  },
  updateSubscription: async (subscription: PayPerLeadSubscription) => {
    const serverModel = SubscriptionConverter.toServerModel(subscription);

    const response: SubscriptionServerModel = await request(
      METHODS.PUT,
      API.PAY_PER_LEAD.UPDATE_SUBSCRIPTION,
    )({
      id: serverModel.id,
      renew: serverModel.renew,
      items: serverModel.items.map((item) => ({
        ...item,
        counties: item.counties.map((c) => ({ id: c.id })),
      })),
    });

    return SubscriptionConverter.fromServerModel(response);
  },
  updateSubscriptionBilling: async (data: UpdateSubscriptionBillingPayload) =>
    request(METHODS.PATCH, API.PAY_PER_LEAD.UPDATE_SUBSCRIPTION_BILLING)(data),
  purchaseSubscription: async ({ id, ...data }: { id: number; paymentMethodId?: string }) => {
    await request(METHODS.POST, API.PAY_PER_LEAD.PURCHASE_SUBSCRIPTION(id))(data);
  },
  setDiscount: ({ id, coupon }: SetDiscountPayload): Promise<ChargeQuote> => {
    return request(METHODS.PUT, API.PAY_PER_LEAD.SET_DISCOUNT(id))({ coupon });
  },
  getChargeQuote: (id: number): Promise<ChargeQuote> => request(METHODS.GET, API.PAY_PER_LEAD.GET_QUOTE(id))(),
  deactivateSubscription: async (id: number, data: Record<string, string>) => {
    await request(METHODS.DELETE, API.PAY_PER_LEAD.GET_SUBSCRIPTION(id))({ data });
  },
  checkPurchase: async (id: number): Promise<CheckPurchaseResponse> =>
    request(METHODS.GET, API.PAY_PER_LEAD.PURCHASE_SUBSCRIPTION(id))(),
  getDisputes: (params: GetDisputesRequest) => {
    return request(METHODS.GET, API.PAY_PER_LEAD.GET_ADMIN_DISPUTES)({ params });
  },
  updateDispute: ({ disputeId, ...params }: UpdateDisputeRequest): Promise<UpdateDisputeResponse> => {
    return request(METHODS.PUT, API.PAY_PER_LEAD.UPDATE_ADMIN_DISPUTES(disputeId))(params);
  },
  qualifyLeads: (params: QualifyLeadsRequest): Promise<void> =>
    request(METHODS.PUT, API.PAY_PER_LEAD.BATCH_QUALIFY_LEADS)(params),
  exportLeads: (params: DownloadLeadsRequestPayload): Promise<Blob> =>
    request(METHODS.POST, API.PAY_PER_LEAD.BATCH_EXPORT_LEADS)(params, {
      responseType: "blob",
    }),
  getSubscriptionsWithCustomers: (params: BaseFilter) =>
    request(METHODS.GET, API.PAY_PER_LEAD.GET_ADMIN_CUSTOMERS)({ params }),
  getStatistics: (params: PayPerLeadStatisticsFilter) =>
    request(METHODS.GET, API.PAY_PER_LEAD.GET_STATISTICS)({ params }),
  getFilterOptions: ({
    account_id,
    is_inactive,
    is_full_range,
  }: {
    account_id?: number | undefined;
    is_inactive: boolean;
    is_full_range: boolean;
  }) => {
    const params = account_id ? { account_id } : {};
    return request(
      METHODS.GET,
      API.PAY_PER_LEAD.GET_FILTER_OPTIONS,
    )({ params: { ...params, is_inactive, is_full_range } });
  },
  getSubscriptionStatistics: (args: GetSubscriptionStatisticsRequest) => {
    const { subscription_id, ...params } = args;
    return request(METHODS.GET, API.PAY_PER_LEAD.GET_SUBSCRIPTION_STATISTICS)({ params });
  },
  applyNewPrices: async (body: ApplyNewPrices) => {
    await request(METHODS.POST, API.PAY_PER_LEAD.APPLY_NEW_PRICES)(body);
  },
  pauseSubscription: async ({ id, is_paused }: { id: number; is_paused: boolean }) => {
    await request(METHODS.PUT, API.PAY_PER_LEAD.PAUSE_SUBSCRIPTION(id))({ is_paused });
  },
  getBuyerProfile: (accountId: number): Promise<PayPerLeadBuyerProfile> =>
    request(METHODS.GET, API.PAY_PER_LEAD.GET_BUYER_PROFILE(accountId))(),
  createBuyerProfile: ({ account_id, buyerProfile }: BuyerRequest): Promise<PayPerLeadBuyerProfile> =>
    request(METHODS.POST, API.PAY_PER_LEAD.CREATE_BUYER_PROFILE(account_id))(buyerProfile),
  updateBuyerProfile: ({ account_id, buyerProfile }: BuyerRequest): Promise<void> =>
    request(METHODS.PUT, API.PAY_PER_LEAD.UPDATE_BUYER_PROFILE(account_id))(buyerProfile),
};
